import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import CmsInlineEdit from '../../../components/Cms/CmsInlineEdit';
import { createMarkdownComponents, findCmsNode } from '../helpers';

const LimitedTimeCards = ({ limitedTimeCards, onButtonClick }) => {
  return (
    <>
      {/* Mobile: Horizontal scrollable */}
      <div
        className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 no-scrollbar"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'manipulation',
          scrollPaddingLeft: '1rem'
        }}
      >
        <div className="flex gap-4 px-4" style={{ width: 'max-content' }}>
          {limitedTimeCards.map((card, index) => (
            <CmsInlineEdit key={card.cmsKeyId || index} type="list" cmsNode={card.cmsNode || null} showContent={false} showChilds>
              <div
                className="flex-shrink-0 rounded-xl p-4 flex flex-col justify-between items-center gap-4 duration-300 group overflow-hidden"
                style={{
                  backgroundColor: card.backgroundColor || '#FFFFFF',
                  scrollSnapAlign: 'start',
                  width: '312px',
                  maxWidth: '312px',
                  height: '520px',
                  maxHeight: '520px',
                  boxSizing: 'border-box',
                  marginLeft: index === 0 ? '0' : undefined
                }}
              >
                {card.image && (
                  <CmsInlineEdit
                    cmsNode={findCmsNode(card.cmsNode, 'image')}
                    applyMarkdown={false}
                    applyMarkdownOnEdit={false}
                  >
                    <ReactMarkdown
                      components={createMarkdownComponents('object-cover rounded-lg flex-shrink-0 w-[280px] h-[220px]')}
                    >
                      {card.image}
                    </ReactMarkdown>
                  </CmsInlineEdit>
                )}
                <div className="flex-1 w-[280px] text-center min-w-0 flex flex-col" style={{ maxWidth: '280px', boxSizing: 'border-box' }}>
                  <h3 className="font-congenialBlack text-[28px] leading-[28px] text-dark font-black tracking-[-0.2px]" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', width: '100%', maxWidth: '100%' }}>
                    <CmsInlineEdit cmsNode={findCmsNode(card.cmsNode, 'title')}>
                      {card.title}
                    </CmsInlineEdit>
                  </h3>
                  <p className="font-montserratMedium font-medium text-sm leading-4 tracking-[0px] text-dark py-4 flex-1" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto', width: '100%', maxWidth: '100%' }}>
                    <CmsInlineEdit cmsNode={findCmsNode(card.cmsNode, 'description')}>
                      {card.description}
                    </CmsInlineEdit>
                  </p>
                  <CmsInlineEdit
                    type="list"
                    cmsNode={card.buttonNode || null}
                    showContent={false}
                    showChilds
                  >
                    <div className='py-4'>
                      <button
                        type='button'
                        onClick={() => onButtonClick(card.buttonLink, card.buttonType, card.buttonLabel || card.buttonText, 'For Limited Time Only')}
                        className="font-montserratMedium font-bold text-lg leading-[22px] !text-dark !bg-transparent !px-0 !w-full"
                      >
                        {card.buttonLabel}
                      </button>
                    </div>
                  </CmsInlineEdit>
                </div>
              </div>
            </CmsInlineEdit>
          ))}
        </div>
      </div>

      {/* Desktop: Vertical stack */}
      <div className="hidden md:flex flex-col gap-6 md:gap-11 xl:gap-16 max-w-7xl mx-auto">
        {limitedTimeCards.map((card, index) => (
          <CmsInlineEdit key={card.cmsKeyId || index} type="list" cmsNode={card.cmsNode || null} showContent={false} showChilds>
            <div
              className="w-full rounded-xl p-6 xl:pr-[60px] flex flex-col md:flex-row items-center gap-6 md:gap-8 xl:gap-10 duration-300 xl:min-h-[395px] group"
              style={{ backgroundColor: card.backgroundColor || '#FFFFFF' }}
            >
              {card.image && (
                <CmsInlineEdit
                  cmsNode={findCmsNode(card.cmsNode, 'image')}
                  applyMarkdown={false}
                  applyMarkdownOnEdit={false}
                >
                  <ReactMarkdown
                    components={createMarkdownComponents('object-cover rounded-lg flex-shrink-0 h-[300px] md:h-[400px] xl:h-[450px] w-[300px] md:w-[400px] xl:w-[450px] xl:mt-[-100px] xl:mb-[-100px] group-hover:scale-110 transition-transform duration-300')}
                  >
                    {card.image}
                  </ReactMarkdown>
                </CmsInlineEdit>
              )}
              <div className="flex-1 xl:min-h-[195px]">
                <h3 className="font-congenialBlack text-2xl md:text-4xl xl:text-[44px] mb-3 text-dark font-black tracking-[-1px]">
                  <CmsInlineEdit cmsNode={findCmsNode(card.cmsNode, 'title')}>
                    {card.title}
                  </CmsInlineEdit>
                </h3>
                <p className="font-montserratMedium font-medium text-sm md:text-base leading-relaxed md:leading-[20px] text-dark xl:pr-4">
                  <CmsInlineEdit cmsNode={findCmsNode(card.cmsNode, 'description')}>
                    {card.description}
                  </CmsInlineEdit>
                </p>
                <CmsInlineEdit
                  type="list"
                  cmsNode={card.buttonNode || null}
                  showContent={false}
                  showChilds
                >
                  <div className='py-4'>
                    <button
                      type='button'
                      onClick={() => onButtonClick(card.buttonLink, card.buttonType, card.buttonLabel || card.buttonText, 'For Limited Time Only')}
                      className="font-montserratMedium font-bold text-lg leading-[22px] !text-dark !bg-transparent !px-0 !w-full text-left"
                    >
                      {card.buttonLabel}
                    </button>
                  </div>
                </CmsInlineEdit>
              </div>
            </div>
          </CmsInlineEdit>
        ))}
      </div>
    </>
  );
};

LimitedTimeCards.propTypes = {
  limitedTimeCards: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    backgroundColor: PropTypes.string,
    cmsKeyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cmsNode: PropTypes.shape({})
  })).isRequired,
  onButtonClick: PropTypes.func.isRequired
};

const LimitedTimeSection = ({ limitedTimeSection, onButtonClick }) => {
  if (!limitedTimeSection || !limitedTimeSection.limitedTimeCards || limitedTimeSection.limitedTimeCards.length === 0) {
    return null;
  }

  const titleNode = findCmsNode(limitedTimeSection.cmsNode, 'title');

  return (
    <CmsInlineEdit type="list" cmsNode={limitedTimeSection.cmsNode || null} showContent={false} showChilds>
      <section className="pb-10 md:pb-20 pt-16 lg:pt-20 xl:pt-32">
        <div className="text-center mb-5 xl:mb-10">
          {limitedTimeSection.title && (
            <h2 className="font-aryaDouble !font-normal text-3xl md:text-5xl tracking-[-1px] text-center text-dark">
              <CmsInlineEdit cmsNode={titleNode || null}>
                {limitedTimeSection.title}
              </CmsInlineEdit>
            </h2>
          )}
        </div>
        <LimitedTimeCards limitedTimeCards={limitedTimeSection.limitedTimeCards} onButtonClick={onButtonClick} />
      </section>
    </CmsInlineEdit>
  );
};

LimitedTimeSection.propTypes = {
  limitedTimeSection: PropTypes.shape({
    title: PropTypes.string,
    dateText: PropTypes.string,
    limitedTimeCards: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
      backgroundColor: PropTypes.string,
      cmsKeyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cmsNode: PropTypes.shape({})
    })),
    cmsNode: PropTypes.shape({})
  }),
  onButtonClick: PropTypes.func.isRequired
};

LimitedTimeSection.defaultProps = {
  limitedTimeSection: null
};

export default LimitedTimeSection;

